import React, { useContext, useState } from 'react';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { Button, Input, Label, TextArea } from 'edozvola-frontend-lib/dist/ui';
import { Col, FormGroup, Row } from 'reactstrap';
import {CaseCaseRegistryModel, CaseCaseRegistryModelV2} from 'app/edozvola/cases/cases.model';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { IRootState, useAppDispatch } from 'app/config/store';
import { Loader } from 'app/shared/loader/loader';
import { CaseTabContext } from 'app/edozvola/cases/case-form-wrapper';
import { CodebookModel } from 'app/shared/model/shared.model';
import { queryClient } from 'app/app';
import { caseRegistryFormV2Validation } from 'app/edozvola/cases/case-case-registry-form/model/case-case-registry.schema';
import { ConfirmationModal } from 'app/shared/modal/confirmation-modal';
import { fetchCaseCaseRegistryQuery, saveCaseCaseRegistryMutation } from './service/case-registry.queries';
import { fetchCaseCategoryCodebookForCaseGroupQuery, fetchCaseGroupCodebookQuery } from 'app/edozvola/codebooks/codebooks.queries';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { getCaseContext } from 'app/shared/util/context-util';

const CaseCaseRegistryForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const appDispatch = useAppDispatch();
  const selectedCase = useSelector((state: IRootState) => state.caseEntity);
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);

  const { sidebarItemPair } = getCaseContext();

  const { data: caseRegistryData, isLoading: caseRegistryDataLoading } = fetchCaseCaseRegistryQuery(id);

  const navigateToCases = (data: any) => {
    if (selectedCase.caseCategory !== data.caseCategory[0].value) {
      navigate('/cases');
    } else {
      formik.resetForm({values: data})
    }
  };

  const { mutate: saveCaseCaseRegistry, isPending: savingInProgress } = saveCaseCaseRegistryMutation(id, navigateToCases);

  const checkIfCaseCategoryIsChanged = values => {
    if (selectedCase.caseCategory !== values.caseCategory[0].value) {
      setConfirmationModalOpened(true);
    } else {
      saveCaseCaseRegistry(values);
    }
  };

  const formik = useFormik({
    initialValues: caseRegistryData || ({} as CaseCaseRegistryModelV2),
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: caseRegistryFormV2Validation,
    onSubmit: values => checkIfCaseCategoryIsChanged(values),
  });

  const { data: caseGroupOptions, isLoading: caseGroupOptionsLoading } = fetchCaseGroupCodebookQuery();

  const { data: caseCategoryOptions, isLoading: caseCategoryOptionsLoading } = fetchCaseCategoryCodebookForCaseGroupQuery(
    formik.values.caseGroup,
    false,
    true,
    false
  );

  const onCaseGroupChanged = (selectedGroup: CodebookModel[]) => {
    formik
      .setValues({
        ...formik.values,
        caseGroup: selectedGroup,
        caseCategory: [],
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['codebook', 'case-category-for-case-group-query'] });
      });
  };

  return (
    <>
      {caseRegistryDataLoading && <Loader />}
      {!caseRegistryDataLoading && (
        <>
          <FormikProvider value={formik}>
            <Form>
              <FormikValueObserver
                onChange={dirty => {
                  if (dirty.dirty) {
                    appDispatch(setFormChanged(true));
                  } else {
                    appDispatch(initCaseFormChange());
                  }
                }}
              />
              <Row>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'Grupa predmeta'} />
                    <Field
                      id="caseGroup"
                      name="caseGroup"
                      render={({ field }) => (
                        <PaginatedDropdown
                          placeholder={!sidebarItemPair?.readOnly && 'Odaberi grupu predmeta'}
                          onItemSelect={onCaseGroupChanged}
                          selected={field.value ?? []}
                          labelKey={'value'}
                          options={caseGroupOptionsLoading ? [] : caseGroupOptions}
                          disabled={sidebarItemPair?.readOnly}
                          error={!sidebarItemPair?.readOnly && formik.errors.caseGroup as string}
                          showErrorMessage
                        />
                      )}
                    />
                  </FormGroup>
                </Col>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'Vrsta predmeta'} />
                    <Field
                      id="caseCategory"
                      name="caseCategory"
                      render={({ field }) => (
                        <PaginatedDropdown
                          placeholder={!sidebarItemPair?.readOnly && 'Odaberi vrstu predmeta'}
                          onItemSelect={selectedOption => formik.setFieldValue('caseCategory', selectedOption)}
                          selected={field.value ?? []}
                          labelKey={'value'}
                          options={caseCategoryOptionsLoading ? [] : caseCategoryOptions}
                          disabled={sidebarItemPair?.readOnly}
                          error={!sidebarItemPair?.readOnly && formik.errors.caseCategory as string}
                          showErrorMessage
                        />
                      )}
                    />
                  </FormGroup>
                </Col>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'Šifra predmeta'} />
                    <Field id="webId" name="webId" render={({ field }) => <Input {...field} disabled={true} type="input" />} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'Klasifikacijska oznaka'} />
                    <Field
                      id="classification"
                      name="classification"
                      render={({ field }) => (
                        <Input {...field} disabled={sidebarItemPair?.readOnly || !selectedCase.earhiva} type="input" />
                      )}
                    />
                  </FormGroup>
                </Col>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'Vrsta postupka'} />
                    <Field
                      id="caseTypeClassification"
                      name="caseTypeClassification"
                      render={({ field }) => <Input {...field} disabled={true} type="input" />}
                    />
                  </FormGroup>
                </Col>
                <Col xs={4}>
                  <FormGroup>
                    <Label labelText={'Kratki opis'} />
                    <Field
                      id="description"
                      name="description"
                      render={({ field }) => (
                        <TextArea {...field} type="input" error={!sidebarItemPair.readOnly && formik.errors.description}
                                  disabled={sidebarItemPair.readOnly} />
                      )}
                    />
                  </FormGroup>
                </Col>
              </Row>
              {!sidebarItemPair.readOnly && (
                <div className="case-save-button">
                  <Button
                    color={'info'}
                    label={'Spremi'}
                    isLoading={savingInProgress}
                    size={'lg'}
                    onClick={formik.submitForm}
                    disabled={!formik.dirty || !formik.isValid}
                  />
                </div>
              )}
            </Form>
          </FormikProvider>
        </>
      )}
      <ConfirmationModal
        modalOpened={confirmationModalOpened}
        header={'Promjena vrste predmeta'}
        bodyMessage={
          'Promjenom vrste predmeta izmijenit će se prikaz kartica na predmetu te ćete biti preusmjereni na početni ekran s popisom predmeta! Želite li potvrditi promjenu vrste predmeta?'
        }
        onConfirm={() => saveCaseCaseRegistry(formik.values)}
        onCancel={() => setConfirmationModalOpened(false)}
      />
    </>
  );
};

export default CaseCaseRegistryForm;
